import jsPDF from "jspdf";
import type { Tip, SessionMetrics } from "./computeMetrics";

// Color palette
const C = {
  bg:     [6,  11,  24] as [number,number,number],
  bg2:    [10, 16,  32] as [number,number,number],
  cyan:   [0,  200, 255] as [number,number,number],
  purple: [139,92,  246] as [number,number,number],
  white:  [240,244, 255] as [number,number,number],
  muted:  [100,116, 139] as [number,number,number],
  green:  [34, 197, 94]  as [number,number,number],
  amber:  [245,158, 11]  as [number,number,number],
  red:    [239,68,  68]  as [number,number,number],
};

function scoreColor(v: number): [number,number,number] {
  if (v >= 80) return C.green;
  if (v >= 65) return C.cyan;
  return C.amber;
}

function drawRect(doc: jsPDF, x: number, y: number, w: number, h: number,
    rgb: [number,number,number], r = 0) {
  doc.setFillColor(...rgb);
  if (r > 0) doc.roundedRect(x, y, w, h, r, r, "F");
  else doc.rect(x, y, w, h, "F");
}

function drawScoreBar(doc: jsPDF, label: string, value: number,
    x: number, y: number, maxW = 200) {
  const col = scoreColor(value);
  // Label
  doc.setFontSize(9);
  doc.setTextColor(...C.muted);
  doc.text(label, x, y + 7);
  // Track
  drawRect(doc, x + 78, y, maxW, 10, [20, 27, 50]);
  // Fill
  drawRect(doc, x + 78, y, (value / 100) * maxW, 10, col);
  // Value
  doc.setFontSize(9);
  doc.setTextColor(...col);
  doc.text(`${value}`, x + 78 + maxW + 8, y + 7);
}

function gradientText(doc: jsPDF, text: string, x: number, y: number, size: number) {
  doc.setFontSize(size);
  doc.setTextColor(...C.cyan);
  doc.text(text, x, y);
}

export function downloadSessionPdf(opts: {
  playerName: string;
  playerEmail: string;
  session: SessionMetrics;
  tips: Tip[];
  handedness?: string;
  level?: string;
}) {
  const { playerName, playerEmail, session, tips, handedness = "right", level = "" } = opts;

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const PW = 595, PH = 842;
  const M = 36;

  // ── PAGE 1 ────────────────────────────────────────────────────────────────

  // Full dark background
  drawRect(doc, 0, 0, PW, PH, C.bg);

  // Header band
  drawRect(doc, 0, 0, PW, 80, C.bg2);

  // Accent left stripe
  drawRect(doc, 0, 0, 4, 80, C.cyan);

  // Logo
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...C.cyan);
  doc.text("SHUTTLECOCK", M + 8, 32);
  doc.setFontSize(8);
  doc.setTextColor(...C.muted);
  doc.text("BY STATVO", M + 8, 44);

  // Session label top-right
  doc.setFontSize(10);
  doc.setTextColor(...C.muted);
  doc.text("SESSION REPORT", PW - M - 2, 28, { align: "right" });
  doc.setFontSize(14);
  doc.setTextColor(...C.white);
  doc.text(session.label, PW - M - 2, 44, { align: "right" });
  doc.setFontSize(9);
  doc.setTextColor(...C.muted);
  doc.text(session.date, PW - M - 2, 56, { align: "right" });

  // Separator line
  doc.setDrawColor(...C.cyan);
  doc.setLineWidth(0.5);
  doc.line(M, 84, PW - M, 84);

  // Player section
  let y = 108;
  drawRect(doc, M, y - 14, PW - M * 2, 50, C.bg2, 8);

  // Avatar circle
  const ax = M + 26, ay = y + 10;
  doc.setFillColor(...C.cyan);
  doc.circle(ax, ay, 18, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(...C.bg);
  const ini = playerName.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  doc.text(ini, ax, ay + 4, { align: "center" });

  doc.setFontSize(14);
  doc.setTextColor(...C.white);
  doc.setFont("helvetica", "bold");
  doc.text(playerName, M + 54, y + 4);
  doc.setFontSize(9);
  doc.setTextColor(...C.muted);
  doc.setFont("helvetica", "normal");
  const meta = [playerEmail, level, handedness + "-handed"].filter(Boolean).join("  ·  ");
  doc.text(meta, M + 54, y + 17);

  // Session stats chips
  const chips = [
    { l: "Duration", v: session.duration },
    { l: "Shots", v: `${session.shots}` },
    { l: "Jump", v: `${session.jumpHeight} cm` },
    { l: "Posture", v: `${session.postureScore}/100` },
  ];
  chips.forEach((chip, i) => {
    const cx = PW - M - 200 + i * 52;
    drawRect(doc, cx, y - 12, 46, 46, [15, 23, 42], 6);
    doc.setFontSize(13);
    doc.setTextColor(...C.cyan);
    doc.setFont("helvetica", "bold");
    doc.text(chip.v, cx + 23, y + 6, { align: "center" });
    doc.setFontSize(7);
    doc.setTextColor(...C.muted);
    doc.setFont("helvetica", "normal");
    doc.text(chip.l.toUpperCase(), cx + 23, y + 18, { align: "center" });
  });

  y += 56;

  // Overall score big ring (drawn with arc lines)
  const ox = M + 50, oy = y + 50;
  const oScore = session.overall;
  doc.setFillColor(...C.bg2);
  doc.circle(ox, oy, 40, "F");
  doc.setDrawColor(20, 27, 50);
  doc.setLineWidth(6);
  doc.circle(ox, oy, 34, "S");
  const col = scoreColor(oScore);
  doc.setDrawColor(...col);
  doc.setLineWidth(6);
  // arc approximation — draw partial circle via lines
  const steps = Math.round(oScore / 100 * 60);
  const R = 34;
  for (let i = 0; i < steps; i++) {
    const a1 = (-Math.PI / 2) + (i / 60) * 2 * Math.PI;
    const a2 = (-Math.PI / 2) + ((i + 1) / 60) * 2 * Math.PI;
    doc.line(ox + R * Math.cos(a1), oy + R * Math.sin(a1),
             ox + R * Math.cos(a2), oy + R * Math.sin(a2));
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(...col);
  doc.text(`${oScore}`, ox, oy + 7, { align: "center" });
  doc.setFontSize(7);
  doc.setTextColor(...C.muted);
  doc.text("OVERALL", ox, oy + 18, { align: "center" });

  // Form scores
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...C.white);
  doc.text("FORM SCORES", M + 104, y + 8);

  const scores: [string, number][] = [
    ["Smash", session.smash], ["Footwork", session.footwork],
    ["Landing", session.landing], ["Prep / Swing", session.prep],
  ];
  scores.forEach(([k, v], i) => {
    drawScoreBar(doc, k, v, M + 104, y + 16 + i * 20, 160);
  });

  y += 118;

  // ── Two-column metrics section ────────────────────────────────────────────
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.setTextColor(...C.white);
  doc.text("MOVEMENT METRICS", M, y);
  y += 14;

  const colW = (PW - M * 2 - 16) / 2;

  const metricsL: [string, string][] = [
    ["Jump Height", `${session.jumpHeight} cm`],
    ["Hang Time", `${session.hangTime} s`],
    ["Displacement", `${session.displacement} cm`],
    ["Direction Changes", `${session.dirChanges}`],
    ["Lunge Count", `${session.lungeCount}`],
    ["Lunge Depth", `${session.lungeDepth}°`],
  ];
  const metricsR: [string, string][] = [
    ["Posture Score", `${session.postureScore}/100`],
    ["Back Arch", `${session.backArch}°`],
    ["Knee Bend", `${session.kneeBend}°`],
    ["Stance Width", `${session.stanceWidth} cm`],
    ["Wrist Speed", `${session.wristSpeed}`],
    ["Recovery Time", `${session.recoveryTime} s`],
  ];

  function drawMetricCard(label: string, val: string, x: number, cy: number) {
    drawRect(doc, x, cy, colW, 22, C.bg2, 4);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...C.muted);
    doc.text(label, x + 10, cy + 9);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(...C.cyan);
    doc.text(val, x + colW - 10, cy + 14, { align: "right" });
  }

  const startY = y;
  metricsL.forEach(([l, v], i) => drawMetricCard(l, v, M, startY + i * 26));
  metricsR.forEach(([l, v], i) => drawMetricCard(l, v, M + colW + 16, startY + i * 26));

  y = startY + metricsL.length * 26 + 16;

  // ── PAGE 2 — AI Suggestions ────────────────────────────────────────────────
  if (tips.length > 0) {
    doc.addPage();
    drawRect(doc, 0, 0, PW, PH, C.bg);
    drawRect(doc, 0, 0, PW, 56, C.bg2);
    drawRect(doc, 0, 0, 4, 56, C.purple);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(...C.purple);
    doc.text("AI COACHING TIPS", M + 8, 28);
    doc.setFontSize(9);
    doc.setTextColor(...C.muted);
    doc.text(`${tips.length} recommendations for ${playerName}`, M + 8, 42);

    let ty = 76;
    tips.slice(0, 8).forEach((t, i) => {
      if (ty > 780) { doc.addPage(); drawRect(doc, 0, 0, PW, PH, C.bg); ty = M; }

      // Card bg
      drawRect(doc, M, ty, PW - M * 2, 0, C.bg2, 6); // will size below
      const cardH = 20 + doc.splitTextToSize(t.desc, PW - M * 2 - 60).length * 13 + 16;
      drawRect(doc, M, ty, PW - M * 2, cardH, C.bg2, 6);

      // Priority stripe
      const stripeCol = i < 2 ? C.red : i < 5 ? C.amber : C.green;
      drawRect(doc, M, ty, 3, cardH, stripeCol, 0);

      // Icon
      doc.setFontSize(16);
      doc.setTextColor(...C.white);
      doc.text(t.icon, M + 16, ty + 18);

      // Title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(...C.white);
      doc.text(t.title, M + 36, ty + 16);

      // Priority badge
      const pLabel = i < 2 ? "HIGH" : i < 5 ? "MEDIUM" : "LOW";
      doc.setFontSize(7);
      doc.setTextColor(...stripeCol);
      doc.text(pLabel, PW - M - 4, ty + 12, { align: "right" });

      // Description
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(...C.muted);
      const lines = doc.splitTextToSize(t.desc, PW - M * 2 - 54);
      doc.text(lines, M + 36, ty + 30);

      ty += cardH + 10;
    });

    // Footer
    drawRect(doc, 0, PH - 30, PW, 30, C.bg2);
    doc.setFontSize(8);
    doc.setTextColor(...C.muted);
    doc.text("Generated by SHUTTLECOCK by STATVO", M, PH - 12);
    doc.text(session.date, PW - M, PH - 12, { align: "right" });
  }

  const safeDate  = (session.date  || "date").replace(/[^\w-]+/g, "-");
  const safeLabel = (session.label || "Session").replace(/[^\w-]+/g, "-");
  doc.save(`Shuttlecock_${safeLabel}_${safeDate}.pdf`);
}